import { useMemo } from "react";
import logo from "@/assets/branding/logo.svg";
import recordingIcon from "@/assets/interview/header/recording.svg";
import iconNetworkStrong from "@/assets/interview/header/network/strong.svg";
import iconNetworkModerate from "@/assets/interview/header/network/moderate.svg";
import iconNetworkPoor from "@/assets/interview/header/network/poor.svg";
import { useInterviewTimer } from "../../contexts/InterviewTimerContext";
import { useNetworkQuality } from "../../contexts/NetworkQualityContext";

interface HeaderProps {
  title?: string;
}

const Header = ({ title = "Technical Interview" }: HeaderProps) => {
  const { totalSeconds } = useInterviewTimer();
  const displayTime = useMemo(() => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes
        .toString()
        .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`;
  }, [totalSeconds]);

  const RecordingTimeNetwork = () => {
    const { state } = useNetworkQuality();

    const networkInfo = useMemo(() => {
      if (state.status === 'green') {
        return {
          label: 'Strong',
          icon: iconNetworkStrong,
          text: '#03965B',
          bg: '#EFFFF9',
        };
      }
      if (state.status === 'orange') {
        return {
          label: 'Moderate',
          icon: iconNetworkModerate,
          text: '#D48A0C',
          bg: '#FFF2DB',
        };
      }
      return {
        label: 'Poor',
        icon: iconNetworkPoor,
        text: '#AA040F',
        bg: '#FFE5E7',
      };
    }, [state.status]);

    return (
      <div className="flex h-9 md:h-7 lg:h-8 items-center gap-3 md:gap-2 rounded-[8px] border border-black/10 bg-white px-3 md:px-2">
        <div className="flex items-center gap-2 md:gap-1.5">
          <img src={recordingIcon} className="w-[19px] h-[19px] md:w-[12px] md:h-[12px] lg:w-[16px] lg:h-[16px] animate-pulse" alt="Recording" />
          <span className="font-dm-sans text-[#68696B] font-medium text-[14px] md:text-[9px] lg:text-[10px] xl:text-[11px] 2xl:text-[12px] leading-[21px] tracking-[-0.14px]">REC</span>
          <span className="font-dm-sans text-[#202326] font-black text-[16px] md:text-[10px] lg:text-[12px] xl:text-[12px] 2xl:text-[13px] leading-[32px] md:leading-[18px] tracking-[0.24px]">{displayTime}</span>
        </div>
        <div className="w-px h-3 md:h-2.5 bg-black/10" />
        <div className="flex items-center">
          <div
            className="flex items-center gap-[6px] rounded-[20px] py-[2px] pr-3 pl-2 md:px-2 md:py-0 lg:py-0"
            style={{ backgroundColor: networkInfo.bg }}
          >
            <img src={networkInfo.icon} className="size-[14px] md:size-[8px] lg:size-[9px]" />
            <span
              className="font-dm-sans font-medium text-[12px] md:text-[7px] lg:text-[9px] leading-[15px] tracking-[-0.12px]"
              style={{ color: networkInfo.text }}
            >
              {networkInfo.label}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="relative flex flex-row justify-between items-center h-full sm:max-h-[90px] lg:max-h-[60px] py-2 md:py-1 lg:py-1.5">
      <div className="flex items-center">
        <p className="text-[20px] font-medium sm:text-[19px] md:text-[14px] lg:text-[15px] xl:text-[18px] tracking-[-0.2px] text-[#202326]">
          {title}
        </p>
      </div>
      <div className="flex items-center gap-3 md:gap-2">
        <RecordingTimeNetwork />
      </div>
      <div className="flex items-center">
        <img src={logo} className="w-[160px] h-auto sm:w-[150px] md:w-[120px] lg:w-[140px]" />
      </div>
    </div>
  );
};

export default Header;
